import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, Clock, Percent, Save, Pencil, X, Users, Search, MoonStar, UserCheck, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  getShifts, saveShift, deleteShift, getPayrollSettings, savePayrollSettings,
  getStaffModules, setStaffModule, setWorkerType,
} from '@/services/payrollApi';

const EMPTY_SHIFT = {
  shift_id: null, name: '', start_time: '09:00', end_time: '18:00',
  from_date: '', to_date: '', multiplier_pct: 0, active: true,
};

const PayrollSettingsTab = () => {
  const [isAdmin, setIsAdmin] = useState(false);

  // Shifts
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_SHIFT);
  const [savingShift, setSavingShift] = useState(false);

  // Cross-module premium
  const [premium, setPremium] = useState('0');
  const [savingPremium, setSavingPremium] = useState(false);

  // Contract-worker uplift
  const [uplift, setUplift] = useState('0');
  const [savingUplift, setSavingUplift] = useState(false);

  // Sunday premium (applies to all staff for work done on Sundays)
  const [sunday, setSunday] = useState('0');
  const [savingSunday, setSavingSunday] = useState(false);

  // Staff home module
  const [modules, setModules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [savingStaff, setSavingStaff] = useState(null);

  const loadShifts = useCallback(async () => {
    setShiftsLoading(true);
    try {
      const d = await getShifts();
      setShifts(d.shifts || []);
      setIsAdmin(!!d.is_admin);
    } catch (e) { toast.error('Failed to load shifts'); }
    finally { setShiftsLoading(false); }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const d = await getPayrollSettings();
      setPremium(String(d.cross_module_premium_pct ?? 0));
      setUplift(String(d.contract_uplift_pct ?? 0));
      setSunday(String(d.sunday_premium_pct ?? 0));
    } catch (e) { /* noop */ }
  }, []);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const d = await getStaffModules();
      setStaff(d.staff || []);
      setModules(d.modules || []);
    } catch (e) { toast.error('Failed to load staff modules'); }
    finally { setStaffLoading(false); }
  }, []);

  useEffect(() => { loadShifts(); loadSettings(); loadStaff(); }, [loadShifts, loadSettings, loadStaff]);

  const startEdit = (s) => setForm({
    shift_id: s.shift_id, name: s.name || '', start_time: s.start_time || '09:00',
    end_time: s.end_time || '18:00', from_date: s.from_date || '', to_date: s.to_date || '',
    multiplier_pct: s.multiplier_pct ?? 0, active: s.active !== false,
  });

  const resetForm = () => setForm(EMPTY_SHIFT);

  const handleSaveShift = async () => {
    if (!form.name.trim()) { toast.error('Shift name is required'); return; }
    if (!form.start_time || !form.end_time) { toast.error('Start & end time are required'); return; }
    const mult = parseFloat(form.multiplier_pct);
    if (isNaN(mult) || mult < 0) { toast.error('Enter a valid multiplier %'); return; }
    setSavingShift(true);
    try {
      await saveShift({
        shift_id: form.shift_id || undefined,
        name: form.name.trim(),
        start_time: form.start_time,
        end_time: form.end_time,
        from_date: form.from_date || null,
        to_date: form.to_date || null,
        multiplier_pct: mult,
        active: !!form.active,
      });
      toast.success(form.shift_id ? 'Shift updated' : 'Shift added');
      resetForm();
      await loadShifts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingShift(false); }
  };

  const handleDeleteShift = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"?`)) return;
    try {
      await deleteShift(s.shift_id);
      toast.success('Shift deleted');
      if (form.shift_id === s.shift_id) resetForm();
      await loadShifts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleSavePremium = async () => {
    const val = parseFloat(premium);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid premium %'); return; }
    setSavingPremium(true);
    try {
      await savePayrollSettings({ crossModulePremiumPct: val });
      toast.success('Cross-module premium saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingPremium(false); }
  };

  const handleSaveUplift = async () => {
    const val = parseFloat(uplift);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid uplift %'); return; }
    setSavingUplift(true);
    try {
      await savePayrollSettings({ contractUpliftPct: val });
      toast.success('Contract worker uplift saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingUplift(false); }
  };

  const handleSaveSunday = async () => {
    const val = parseFloat(sunday);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid Sunday premium %'); return; }
    setSavingSunday(true);
    try {
      await savePayrollSettings({ sundayPremiumPct: val });
      toast.success('Sunday premium saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingSunday(false); }
  };

  const handleWorkerType = async (staffId, workerType) => {
    setSavingStaff(staffId);
    try {
      await setWorkerType({ staffId, workerType });
      setStaff((prev) => prev.map((s) => s.staff_id === staffId ? { ...s, worker_type: workerType } : s));
      toast.success(workerType === 'CONTRACT' ? 'Marked as Contract worker' : 'Marked as Regular worker');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingStaff(null); }
  };

  const handleStaffModule = async (staffId, homeModule) => {
    setSavingStaff(staffId);
    try {
      const value = homeModule === '__none__' ? null : homeModule;
      await setStaffModule({ staffId, homeModule: value });
      setStaff((prev) => prev.map((s) => s.staff_id === staffId ? { ...s, home_module: value } : s));
      toast.success('Home module updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingStaff(null); }
  };

  const filteredStaff = staff.filter((s) => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return true;
    return (s.staff_name || '').toLowerCase().includes(q) || (s.staff_id || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5" data-testid="payroll-settings">
      {!isAdmin && (
        <div className="text-sm text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-2">
          Read-only — admin access is required to edit shifts and premiums.
        </div>
      )}

      {/* Shifts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Shifts</CardTitle>
          <CardDescription>
            Define shifts (IST). A shift whose end time is earlier than its start time crosses midnight
            (e.g. 22:00 → 06:00). The multiplier % is added on top of the base rate for pieces in that shift.
            Changing a shift's time-window or dates needs a Payroll re-sync; changing the % is instant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-auto border rounded-lg" data-testid="shifts-table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A1F2E] text-white text-left">
                  <th className="px-3 py-2 min-w-[140px]">Name</th>
                  <th className="px-3 py-2 text-center">Start</th>
                  <th className="px-3 py-2 text-center">End</th>
                  <th className="px-3 py-2 text-center min-w-[110px]">From date</th>
                  <th className="px-3 py-2 text-center min-w-[110px]">To date</th>
                  <th className="px-3 py-2 text-right">Multiplier</th>
                  <th className="px-3 py-2 text-center">Active</th>
                  {isAdmin && <th className="px-3 py-2 text-center min-w-[110px]">Action</th>}
                </tr>
              </thead>
              <tbody>
                {shiftsLoading ? (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="py-6 text-center text-[#5A5F6E]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                ) : shifts.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="py-6 text-center text-[#5A5F6E]">No shifts configured yet.</td></tr>
                ) : shifts.map((s) => {
                  const overnight = (s.end_time || '') <= (s.start_time || '');
                  return (
                    <tr key={s.shift_id} className="border-t hover:bg-[#F5F1E8]" data-testid={`shift-row-${s.shift_id}`}>
                      <td className="px-3 py-2 font-medium flex items-center gap-1.5">
                        {overnight && <MoonStar className="w-3.5 h-3.5 text-indigo-500" title="Crosses midnight" />}
                        {s.name}
                      </td>
                      <td className="px-3 py-2 text-center">{s.start_time}</td>
                      <td className="px-3 py-2 text-center">{s.end_time}</td>
                      <td className="px-3 py-2 text-center text-[#5A5F6E]">{s.from_date || '—'}</td>
                      <td className="px-3 py-2 text-center text-[#5A5F6E]">{s.to_date || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-[#2D5F4F]">+{s.multiplier_pct || 0}%</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={s.active !== false ? 'default' : 'secondary'}>{s.active !== false ? 'Active' : 'Off'}</Badge>
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(s)} data-testid={`shift-edit-${s.shift_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => handleDeleteShift(s)} data-testid={`shift-delete-${s.shift_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {isAdmin && (
            <div className="border rounded-lg p-4 bg-[#FAFAF7]" data-testid="shift-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm">{form.shift_id ? 'Edit shift' : 'Add a shift'}</h4>
                {form.shift_id && (
                  <Button size="sm" variant="ghost" onClick={resetForm} data-testid="shift-form-cancel"><X className="w-4 h-4 mr-1" />Cancel edit</Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
                <div className="col-span-2 md:col-span-2">
                  <Label className="text-xs">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Night" className="mt-1" data-testid="shift-name" />
                </div>
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" data-testid="shift-start" />
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="shift-end" />
                </div>
                <div>
                  <Label className="text-xs">From date</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1" data-testid="shift-from" />
                </div>
                <div>
                  <Label className="text-xs">To date</Label>
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1" data-testid="shift-to" />
                </div>
                <div>
                  <Label className="text-xs">Multiplier %</Label>
                  <Input type="number" step="1" min="0" value={form.multiplier_pct} onChange={(e) => setForm({ ...form, multiplier_pct: e.target.value })} className="mt-1" data-testid="shift-multiplier" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="shift-active" />
                  Active
                </label>
                <Button onClick={handleSaveShift} disabled={savingShift} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="shift-save">
                  {savingShift ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (form.shift_id ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
                  {form.shift_id ? 'Update shift' : 'Add shift'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cross-module premium */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><Percent className="w-5 h-5" />Cross-module Premium</CardTitle>
          <CardDescription>
            When a staff works in a module that is NOT their assigned home module, their pay for that
            work is increased by this %. Applied on top of any shift premium.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Premium %</Label>
              <Input type="number" step="1" min="0" value={premium} onChange={(e) => setPremium(e.target.value)}
                disabled={!isAdmin} className="mt-1 w-40" data-testid="cross-premium-input" />
            </div>
            {isAdmin && (
              <Button onClick={handleSavePremium} disabled={savingPremium} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="cross-premium-save">
                {savingPremium ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contract worker uplift */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5" />Contract Worker Uplift</CardTitle>
          <CardDescription>
            Contract (non-permanent) workers are paid this % higher than regular rates. It is applied
            on the final computed amount (after any shift / cross-module premium). Set each staff as
            Regular or Contract in the table below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Uplift %</Label>
              <Input type="number" step="1" min="0" value={uplift} onChange={(e) => setUplift(e.target.value)}
                disabled={!isAdmin} className="mt-1 w-40" data-testid="contract-uplift-input" />
            </div>
            {isAdmin && (
              <Button onClick={handleSaveUplift} disabled={savingUplift} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="contract-uplift-save">
                {savingUplift ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sunday premium */}
      <Card data-testid="sunday-premium-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5" />Sunday Premium</CardTitle>
          <CardDescription>
            All staff are paid this % extra for work done on a <b>Sunday</b> (based on the IST work
            date). Applied on top of any shift, cross-module and contract premiums. Set to 0 to
            disable. Changes apply instantly — no re-sync needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Sunday premium %</Label>
              <Input type="number" step="1" min="0" value={sunday} onChange={(e) => setSunday(e.target.value)}
                disabled={!isAdmin} className="mt-1 w-40" data-testid="sunday-premium-input" />
            </div>
            {isAdmin && (
              <Button onClick={handleSaveSunday} disabled={savingSunday} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="sunday-premium-save">
                {savingSunday ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Staff home module */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" />Staff — Home Module & Worker Type</CardTitle>
          <CardDescription>
            Assign each staff to their primary (home) module (work in any other module earns the
            cross-module premium), and mark whether they are a Regular or Contract worker. Staff shown
            are those seen in payroll entries.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5F6E]" />
            <Input value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} placeholder="Search name or ID…" className="pl-8" data-testid="staff-module-search" />
          </div>
          <div className="overflow-auto border rounded-lg max-h-[55vh]" data-testid="staff-modules-table-wrap">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="bg-[#1A1F2E] text-white text-left">
                  <th className="px-4 py-2 min-w-[200px]">Staff</th>
                  <th className="px-4 py-2 min-w-[220px]">Worked in</th>
                  <th className="px-4 py-2 min-w-[220px]">Home module</th>
                  <th className="px-4 py-2 min-w-[170px]">Worker type</th>
                </tr>
              </thead>
              <tbody>
                {staffLoading ? (
                  <tr><td colSpan={4} className="py-6 text-center text-[#5A5F6E]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                ) : filteredStaff.length === 0 ? (
                  <tr><td colSpan={4} className="py-6 text-center text-[#5A5F6E]">No staff found.</td></tr>
                ) : filteredStaff.map((s) => (
                  <tr key={s.staff_id} className="border-t hover:bg-[#F5F1E8]" data-testid={`staff-module-row-${s.staff_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{s.staff_name}</div>
                      <div className="text-xs text-[#5A5F6E]">{s.staff_id}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(s.worked_modules || []).map((m) => (
                          <Badge key={m} variant={m === s.home_module ? 'default' : 'secondary'} className="text-[10px]">{m}</Badge>
                        ))}
                        {(s.worked_modules || []).length === 0 && <span className="text-xs text-gray-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Select value={s.home_module || '__none__'} disabled={!isAdmin || savingStaff === s.staff_id}
                          onValueChange={(v) => handleStaffModule(s.staff_id, v)}>
                          <SelectTrigger className="w-52" data-testid={`staff-module-select-${s.staff_id}`}>
                            <SelectValue placeholder="Not assigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Not assigned</SelectItem>
                            {modules.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        {savingStaff === s.staff_id && <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Select value={s.worker_type || 'REGULAR'} disabled={!isAdmin || savingStaff === s.staff_id}
                        onValueChange={(v) => handleWorkerType(s.staff_id, v)}>
                        <SelectTrigger className="w-40" data-testid={`worker-type-select-${s.staff_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="REGULAR">Regular</SelectItem>
                          <SelectItem value="CONTRACT">Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollSettingsTab;
